// The guided first run, as data: six short steps, each skippable, each with one obvious next
// thing. The page (WelcomePage) renders the current one; this file holds the order and words.

export type StepId = "gmail" | "timeZone" | "briefs" | "vips" | "voice" | "firstRound";

export interface Step {
  id: StepId;
  title: string;
  /** One calm line under the title. */
  lead: string;
  /** What "Skip" means, so skipping never feels like losing something. */
  skipped: string;
}

export const STEPS: readonly Step[] = [
  {
    id: "gmail",
    title: "Connect Gmail",
    lead: "MeeMail reads your mail to sort it into a few decisions. It never sends or deletes anything without you.",
    skipped: "You can connect it later from Accounts.",
  },
  {
    id: "timeZone",
    title: "Your time zone",
    lead: "Briefs, quiet hours and “today” follow it. This browser’s zone is already filled in.",
    skipped: "MeeMail keeps the zone it has now.",
  },
  {
    id: "briefs",
    title: "When should MeeMail check in?",
    lead: "A brief gathers what’s waiting for you at a few set times. Quiet hours keep even urgent mail from announcing itself.",
    skipped: "Briefs stay at the usual times. You can change them in Settings.",
  },
  {
    id: "vips",
    title: "Who always gets through?",
    lead: "Mail from VIPs skips the wait for the next round. These are people you’ve written to most lately.",
    skipped: "Nobody new is added. The VIPs page is in Settings.",
  },
  {
    id: "voice",
    title: "Drafts in your voice",
    lead: "Claude can read some of your sent mail and propose a short description of how you write. You review it before anything changes.",
    skipped: "Drafts use a neutral voice until you teach it. Settings → Writing voice.",
  },
  {
    id: "firstRound",
    title: "Your first round",
    lead: "A round is a short, fixed set of decisions. You can stop at any card and pick up later.",
    skipped: "",
  },
];

export function stepIndex(id: string | undefined): number {
  const i = STEPS.findIndex((s) => s.id === id);
  return i < 0 ? 0 : i;
}

/** The step after `id`, or null after the last one. */
export function nextStep(id: StepId): StepId | null {
  return STEPS[stepIndex(id) + 1]?.id ?? null;
}

export function previousStep(id: StepId): StepId | null {
  const i = stepIndex(id);
  return i > 0 ? STEPS[i - 1]!.id : null;
}

/** "Step 2 of 6": where you are, never how much is left undone. */
export function stepLabel(id: StepId): string {
  return `Step ${stepIndex(id) + 1} of ${STEPS.length}`;
}

/** What the first round covers, in plain words. */
export function firstRoundLine(narrow: boolean): string {
  return narrow
    ? "It starts small: only mail from people in the last week that’s still waiting for you. Newsletters, notifications and older mail stay in Gmail for later."
    : "It holds what MeeMail found, sorted into cards; routine mail is grouped so you can clear it together.";
}
